// DealKanban — tablero de etapas. Escritorio: columnas con arrastrar y soltar.
// Celular (y siempre como alternativa): pestañas de etapa + «Mover a…», sin
// scroll horizontal de la página.
//
// Presentacional: los datos y el movimiento (optimista, con reversa si falla)
// viven en DealsApp.

import { useRef, useState, type KeyboardEvent } from 'react';

import type { Deal, DealStage, FollowUpRow, ForecastStageBucket } from '../types';
import { DealColumn } from './DealColumn';
import { stageStyle } from './parts';

export interface DealKanbanProps {
  stages: DealStage[];
  deals: Deal[];
  followUps: Record<string, FollowUpRow>;
  buckets: Record<number, ForecastStageBucket>;
  forecastReady: boolean;
  currency: string;
  activeId: string | null;
  isMobile: boolean;
  isSeller: boolean;
  mobileStageId: number | null;
  onMobileStage: (id: number) => void;
  onOpen: (d: Deal) => void;
  onMove: (d: Deal, s: DealStage) => void;
  emptyHint?: string;
  footnote?: string;
}

export function DealKanban(p: DealKanbanProps) {
  const dragged = useRef<Deal | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const byStage = new Map<number, Deal[]>();
  for (const s of p.stages) byStage.set(s.id, []);
  for (const d of p.deals) byStage.get(d.stage_id)?.push(d);

  const column = (s: DealStage) => (
    <DealColumn key={s.id} stage={s} deals={byStage.get(s.id) || []} stages={p.stages} bucket={p.buckets[s.id]}
      forecastReady={p.forecastReady} currency={p.currency} followUps={p.followUps} activeId={p.activeId}
      draggingId={draggingId} isMobile={p.isMobile} isSeller={p.isSeller} onOpen={p.onOpen} onMove={p.onMove}
      emptyHint={p.emptyHint}
      onDragStartDeal={(d) => { dragged.current = d; setDraggingId(d.id); }}
      onDragEndDeal={() => { dragged.current = null; setDraggingId(null); }}
      onDropDeal={(stage) => {
        const d = dragged.current;
        dragged.current = null;
        setDraggingId(null);
        if (d && d.stage_id !== stage.id) p.onMove(d, stage);
      }} />
  );

  if (p.isMobile) {
    const current = p.stages.find((s) => s.id === p.mobileStageId) || p.stages[0];
    const onKey = (e: KeyboardEvent) => {
      const i = p.stages.findIndex((s) => s.id === current?.id);
      const n = p.stages.length;
      if (e.key === 'ArrowRight') { e.preventDefault(); p.onMobileStage(p.stages[(i + 1) % n].id); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); p.onMobileStage(p.stages[(i - 1 + n) % n].id); }
    };
    return (
      <div className="flex h-full min-h-0 flex-col gap-2 p-3">
        <div role="tablist" aria-label="Etapas del pipeline" onKeyDown={onKey}
          className="grid grid-cols-3 gap-1 rounded-xl border border-border bg-muted p-1">
          {p.stages.map((s) => {
            const sel = s.id === current?.id;
            const n = byStage.get(s.id)?.length || 0;
            return (
              <button key={s.id} type="button" role="tab" aria-selected={sel} tabIndex={sel ? 0 : -1}
                onClick={() => p.onMobileStage(s.id)} style={stageStyle(s)}
                className={`flex min-h-[48px] min-w-0 flex-col items-center justify-center rounded-[10px] px-1 py-1.5 ${sel ? 'bg-card shadow-sm' : ''}`}>
                <span className="flex items-center gap-1 text-[15px] font-semibold tabular-nums text-foreground">
                  <span className="dl-dot h-2 w-2 rounded-full" aria-hidden />{n}
                </span>
                <span className={`max-w-full truncate text-[11px] ${sel ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>{s.name}</span>
              </button>
            );
          })}
        </div>
        {current && <div className="min-h-0 flex-1" role="tabpanel" aria-label={current.name}>{column(current)}</div>}
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="dl-scroll flex min-h-0 flex-1 gap-3 overflow-x-auto p-3 sm:p-4">{p.stages.map(column)}</div>
      {p.footnote && <p className="border-t border-border px-4 py-2 text-[11.5px] text-muted-foreground">{p.footnote}</p>}
    </div>
  );
}
